import React, { useState } from 'react';
import { 
  AlertCircle, 
  AlertTriangle, 
  Info, 
  CheckCircle, 
  X, 
  ChevronDown,
  ChevronUp,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export type AlertType = 'error' | 'warning' | 'info' | 'success';
export type AlertPriority = 'high' | 'medium' | 'low';

export interface AlertItem {
  id: string;
  type: AlertType;
  priority: AlertPriority;
  title: string;
  message: string;
  timestamp?: Date;
  actionLabel?: string;
  onAction?: () => void;
  dismissible?: boolean;
}

interface AlertsPanelProps {
  alerts: AlertItem[];
  onDismiss?: (id: string) => void;
  className?: string;
  showTimestamp?: boolean;
  groupByType?: boolean;
  collapsible?: boolean;
}

const alertConfig = {
  error: {
    icon: AlertCircle,
    bgColor: 'bg-red-50 dark:bg-red-950/20',
    borderColor: 'border-red-200 dark:border-red-800',
    iconColor: 'text-red-600 dark:text-red-400',
    textColor: 'text-red-900 dark:text-red-100',
    badgeVariant: 'destructive' as const,
  },
  warning: {
    icon: AlertTriangle,
    bgColor: 'bg-amber-50 dark:bg-amber-950/20',
    borderColor: 'border-amber-200 dark:border-amber-800',
    iconColor: 'text-amber-600 dark:text-amber-400',
    textColor: 'text-amber-900 dark:text-amber-100',
    badgeVariant: 'secondary' as const,
  },
  info: {
    icon: Info,
    bgColor: 'bg-blue-50 dark:bg-blue-950/20',
    borderColor: 'border-blue-200 dark:border-blue-800',
    iconColor: 'text-blue-600 dark:text-blue-400',
    textColor: 'text-blue-900 dark:text-blue-100',
    badgeVariant: 'secondary' as const,
  },
  success: {
    icon: CheckCircle,
    bgColor: 'bg-green-50 dark:bg-green-950/20',
    borderColor: 'border-green-200 dark:border-green-800',
    iconColor: 'text-green-600 dark:text-green-400',
    textColor: 'text-green-900 dark:text-green-100',
    badgeVariant: 'secondary' as const,
  },
};

const priorityConfig = {
  high: {
    label: 'High Priority',
    color: 'text-red-600 dark:text-red-400',
    badgeColor: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300',
  },
  medium: {
    label: 'Medium Priority',
    color: 'text-amber-600 dark:text-amber-400',
    badgeColor: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300',
  },
  low: {
    label: 'Low Priority',
    color: 'text-blue-600 dark:text-blue-400',
    badgeColor: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300',
  },
};

export function AlertsPanel({
  alerts,
  onDismiss,
  className,
  showTimestamp = true,
  groupByType = true,
  collapsible = false,
}: AlertsPanelProps) {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<AlertType>>(new Set());

  const toggleGroup = (type: AlertType) => {
    const newSet = new Set(collapsedGroups);
    if (newSet.has(type)) {
      newSet.delete(type);
    } else {
      newSet.add(type);
    }
    setCollapsedGroups(newSet);
  };

  const groupedAlerts: Record<string, AlertItem[]> = groupByType
    ? alerts.reduce((acc, alert) => {
        if (!acc[alert.type]) acc[alert.type] = [];
        acc[alert.type].push(alert);
        return acc;
      }, {} as Record<string, AlertItem[]>)
    : { all: alerts };

  const sortedTypes = Object.keys(groupedAlerts).sort((a, b) => {
    const order = { error: 0, warning: 1, info: 2, success: 3 };
    return order[a as AlertType] - order[b as AlertType];
  }) as AlertType[];

  const formatTimestamp = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (alerts.length === 0) {
    return (
      <Card className={cn('border-dashed', className)}>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <CheckCircle className="h-12 w-12 text-green-500 mb-3" />
          <p className="text-lg font-semibold text-muted-foreground">All Clear!</p>
          <p className="text-sm text-muted-foreground mt-1">No alerts at this time</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn('space-y-4', className)}>
      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {(['error', 'warning', 'info', 'success'] as AlertType[]).map((type) => {
          const count = groupedAlerts[type]?.length || 0;
          const config = alertConfig[type];
          const Icon = config.icon;

          return (
            <div
              key={type}
              className="animate-in fade-in slide-in-from-bottom-4 duration-300"
              style={{ animationDelay: `${['error', 'warning', 'info', 'success'].indexOf(type) * 50}ms` }}
            >
              <Card className={cn('border-2 transition-all duration-200', config.borderColor, count > 0 && config.bgColor)}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon className={cn('h-5 w-5', config.iconColor)} />
                      <span className="text-sm font-medium capitalize">{type}s</span>
                    </div>
                    <Badge variant={config.badgeVariant} className="text-lg font-bold px-2.5 py-0.5">
                      {count}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          );
        })}
      </div>

      {/* Alerts List */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl">Active Alerts</CardTitle>
            <Badge variant="secondary" className="text-sm">
              {alerts.length} Total
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {sortedTypes.map((type) => {
            const typeAlerts = groupedAlerts[type];
            if (!typeAlerts || typeAlerts.length === 0) return null;

            const config = alertConfig[type];
            const Icon = config.icon;
            const isCollapsed = collapsedGroups.has(type);

            // Sort by priority within each type
            const sortedAlerts = [...typeAlerts].sort((a, b) => {
              const priorityOrder = { high: 0, medium: 1, low: 2 };
              return priorityOrder[a.priority] - priorityOrder[b.priority];
            });

            return (
              <div key={type} className="space-y-2">
                {/* Group Header */}
                {groupByType && collapsible && (
                  <button
                    onClick={() => toggleGroup(type)}
                    className={cn(
                      'w-full flex items-center justify-between p-3 rounded-lg border-2 transition-all duration-200',
                      config.borderColor,
                      config.bgColor,
                      'hover:opacity-80 hover:shadow-sm'
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className={cn('h-5 w-5', config.iconColor)} />
                      <span className={cn('font-semibold capitalize', config.textColor)}>
                        {type}s ({typeAlerts.length})
                      </span>
                    </div>
                    {isCollapsed ? (
                      <ChevronDown className={cn('h-5 w-5 transition-transform', config.iconColor)} />
                    ) : (
                      <ChevronUp className={cn('h-5 w-5 transition-transform', config.iconColor)} />
                    )}
                  </button>
                )}

                {/* Alerts */}
                {(!collapsible || !isCollapsed) && (
                  <div className="space-y-2">
                    {sortedAlerts.map((alert, index) => (
                      <div
                        key={alert.id}
                        className={cn(
                          'relative p-4 rounded-lg border-2 transition-all duration-200',
                          config.borderColor,
                          config.bgColor,
                          'hover:shadow-md hover:scale-[1.01]',
                          'animate-in fade-in slide-in-from-left-4'
                        )}
                        style={{ animationDelay: `${index * 50}ms` }}
                      >
                        <div className="flex items-start gap-3">
                          {/* Icon */}
                          <div className={cn('p-2 rounded-lg bg-white dark:bg-gray-900 shadow-sm')}>
                            <Icon className={cn('h-5 w-5', config.iconColor)} />
                          </div>

                          {/* Content */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-2 mb-1">
                              <h4 className={cn('font-semibold text-sm sm:text-base', config.textColor)}>
                                {alert.title}
                              </h4>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {alert.priority === 'high' && (
                                  <Badge className={priorityConfig.high.badgeColor} variant="secondary">
                                    High
                                  </Badge>
                                )}
                                {alert.dismissible && onDismiss && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 rounded-full hover:bg-white/50 dark:hover:bg-gray-800/50"
                                    onClick={() => onDismiss(alert.id)}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>

                            <p className={cn('text-sm mb-2', config.textColor, 'opacity-90')}>
                              {alert.message}
                            </p>

                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              {showTimestamp && alert.timestamp && (
                                <div className="flex items-center gap-1.5 text-xs opacity-70">
                                  <Clock className="h-3 w-3" />
                                  <span>{formatTimestamp(alert.timestamp)}</span>
                                </div>
                              )}

                              {alert.actionLabel && alert.onAction && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className={cn(
                                    'text-xs h-7 border-2',
                                    config.borderColor,
                                    'hover:bg-white dark:hover:bg-gray-900'
                                  )}
                                  onClick={alert.onAction}
                                >
                                  {alert.actionLabel}
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

// Compact version for inline use
export function CompactAlertsPanel({
  alerts,
  onDismiss,
  className,
  maxVisible = 3,
}: AlertsPanelProps & { maxVisible?: number }) {
  const [showAll, setShowAll] = useState(false);
  const visibleAlerts = showAll ? alerts : alerts.slice(0, maxVisible);
  const hasMore = alerts.length > maxVisible;

  if (alerts.length === 0) return null;

  return (
    <div className={cn('space-y-2', className)}>
      {visibleAlerts.map((alert, index) => {
        const config = alertConfig[alert.type];
        const Icon = config.icon;

        return (
          <div
            key={alert.id}
            className={cn(
              'flex items-start gap-3 p-3 rounded-lg border-2 transition-all duration-200',
              config.borderColor,
              config.bgColor,
              'hover:shadow-sm animate-in fade-in slide-in-from-top-2'
            )}
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <Icon className={cn('h-5 w-5 flex-shrink-0 mt-0.5', config.iconColor)} />
            <div className="flex-1 min-w-0">
              <p className={cn('text-sm font-medium', config.textColor)}>{alert.title}</p>
              <p className={cn('text-xs mt-0.5', config.textColor, 'opacity-80')}>{alert.message}</p>
            </div>
            {alert.dismissible && onDismiss && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0"
                onClick={() => onDismiss(alert.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        );
      })}

      {hasMore && (
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? 'Show Less' : `Show ${alerts.length - maxVisible} More`}
        </Button>
      )}
    </div>
  );
}
